import { createSlice } from "@reduxjs/toolkit";
import { getAllQuestionsForOrder } from "../../../../shared/api/bigQuery/nycbsPharmaOrders";
import {
  PharmaQuestionModel,
  PharmaQuestionsModel,
} from "../../../data-model/pharmaQuestion";
import { logDataToConsole } from "../../../utils/customLogger";

interface PharmaQuestionsSlice {
  data: PharmaQuestionsModel | null;
  loading: boolean;
  error: string | null;
}

const initialState: PharmaQuestionsSlice = {
  data: null,
  loading: false,
  error: null,
};

const pharmaQuestionsSlice = createSlice({
  name: "pharmaQuestionsSlice",
  initialState,
  reducers: {
    fetchPharmaQuestionsStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchPharmaQuestionsSuccess(state, action) {
      state.data = action.payload;
      state.loading = false;
    },
    fetchPharmaQuestionsFailure(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    resetPharmaQuestions(state) {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
  },
});

export const { resetPharmaQuestions } = pharmaQuestionsSlice.actions;

export const fetchAllQuestionFromBigQuery =
  (id: string) => async (dispatch, getState) => {
    const { pharmaQuestions } = getState();
    if (pharmaQuestions.loading) {
      return;
    }

    dispatch(pharmaQuestionsSlice.actions.fetchPharmaQuestionsStart());
    try {
      const result = await getAllQuestionsForOrder(id);

      if (Object.keys(result["details"]).length > 0) {
        logDataToConsole("All questions for order", result);
        const modifiedQuestion = mergeQuestions(result["details"]["questions"]);
        const data = { ...result["details"], questions: modifiedQuestion };
        dispatch(
          pharmaQuestionsSlice.actions.fetchPharmaQuestionsSuccess(data),
        );
      } else {
        dispatch(
          pharmaQuestionsSlice.actions.fetchPharmaQuestionsSuccess(null),
        );
      }
    } catch (error) {
      dispatch(
        pharmaQuestionsSlice.actions.fetchPharmaQuestionsFailure(
          "Error fetching questions",
        ),
      );
    }
  };

const mergeQuestions = (questions: PharmaQuestionModel[]) => {
  const mergedQuestions: PharmaQuestionModel[] = [];

  questions.forEach((question) => {
    const index = mergedQuestions.findIndex(
      (q) => q.question === question.question,
    );

    if (index === -1) {
      mergedQuestions.push({ ...question });
    } else {
      const mergedQuestion = mergedQuestions[index];
      const type = question.type;
      const response = question?.answer ?? "";

      if (type === "multi-choice") {
        mergedQuestion.combined_answer = {
          multiChoiceAnswer: response,
          subjectiveAnswer: mergedQuestion.answer ?? "",
        };
      } else if (type === "subjective") {
        mergedQuestion.combined_answer = {
          multiChoiceAnswer: mergedQuestion.answer ?? "",
          subjectiveAnswer: response,
        };
      }
      mergedQuestion.type = "combined";
    }
  });

  return mergedQuestions;
};

export default pharmaQuestionsSlice.reducer;
